/**
 * RBAC API Hooks - Roles and Permissions management
 *
 * Direct SDK usage for role-based access control operations.
 */

import type {
  PermissionResponse,
  RoleCreate,
  RoleResponse,
  RoleType,
  RoleUpdate,
} from "@/client/types.gen"
import { api } from "@/services/api"
import { unwrapStandardResponse } from "@/utils/apiResponse"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

/**
 * Fetch all roles
 */
export function useRoles(includePermissions = false) {
  return useQuery({
    queryKey: ["roles", { includePermissions }],
    queryFn: async (): Promise<RoleResponse[]> => {
      const response = await api.admin.listRolesApiV1AdminRolesGet({
        includePermissions,
      })
      return (unwrapStandardResponse<RoleResponse[]>(response) ??
        []) as RoleResponse[]
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch single role by ID
 */
export function useRole(roleId: string) {
  return useQuery({
    queryKey: ["roles", roleId],
    queryFn: async () => {
      const response = await api.admin.getRoleApiV1AdminRolesRoleIdGet({
        roleId,
      })
      return response
    },
    enabled: !!roleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Create new role
 */
export function useCreateRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (roleData: {
      name: string
      display_name: string
      description?: string
      role_type: RoleType
      permission_ids?: string[]
    }) => {
      const roleCreate: RoleCreate = {
        name: roleData.name,
        display_name: roleData.display_name,
        description: roleData.description || null,
        role_type: roleData.role_type,
        permission_ids: roleData.permission_ids,
      }
      const response = await api.admin.createRoleApiV1AdminRolesPost({
        requestBody: roleCreate,
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] })
    },
  })
}

/**
 * Update existing role
 */
export function useUpdateRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      roleId,
      roleData,
    }: {
      roleId: string
      roleData: {
        name?: string
        display_name?: string
        description?: string
        permission_ids?: string[]
      }
    }) => {
      const roleUpdate: RoleUpdate = {
        display_name: roleData.display_name,
        description: roleData.description,
        permission_ids: roleData.permission_ids,
      }
      const response = await api.admin.updateRoleApiV1AdminRolesRoleIdPut({
        roleId,
        requestBody: roleUpdate,
      })
      return response
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["roles", variables.roleId] })
      queryClient.invalidateQueries({ queryKey: ["roles"] })
    },
  })
}

/**
 * Delete role
 */
export function useDeleteRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (roleId: string) => {
      try {
        const response = await api.admin.deleteRoleApiV1AdminRolesRoleIdDelete({
          roleId,
        })
        return response
      } catch (error: any) {
        // Extract the error message from the API response
        const message =
          error?.body?.message || error?.message || "Failed to delete role"
        // Create a new Error with the extracted message so it can be caught by the component
        const apiError = new Error(message)
        // Preserve the original error for debugging
        ;(apiError as any).originalError = error
        throw apiError
      }
    },
    onSuccess: (_data, roleId) => {
      queryClient.removeQueries({ queryKey: ["roles", roleId] })
      queryClient.invalidateQueries({ queryKey: ["roles"] })
    },
  })
}

/**
 * Fetch all permissions
 */
export function usePermissions() {
  return useQuery({
    queryKey: ["permissions"],
    queryFn: async (): Promise<PermissionResponse[]> => {
      const response = await api.admin.listPermissionsApiV1AdminPermissionsGet()
      return (unwrapStandardResponse<PermissionResponse[]>(response) ??
        []) as PermissionResponse[]
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (permissions rarely change)
  })
}

/**
 * Fetch user roles
 */
export function useUserRoles(userId: string) {
  return useQuery({
    queryKey: ["users", userId, "roles"],
    queryFn: async (): Promise<RoleResponse[]> => {
      const response =
        await api.admin.getUserRolesApiV1AdminUsersUserIdRolesGet({
          userId,
        })
      return (unwrapStandardResponse<RoleResponse[]>(response) ??
        []) as RoleResponse[]
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Assign role to user
 */
export function useAssignRoleToUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      roleData,
    }: {
      userId: string
      roleData: { role_id: string; assignment_reason: string }
    }) => {
      const response =
        await api.admin.assignRoleToUserApiV1AdminUsersUserIdRolesPost({
          userId,
          requestBody: roleData,
        })
      return response
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["users", variables.userId, "roles"],
      })
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

/**
 * Remove role from user
 */
export function useRemoveRoleFromUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      userId,
      roleId,
    }: { userId: string; roleId: string }) => {
      await api.admin.removeRoleFromUserApiV1AdminUsersUserIdRolesRoleIdDelete({
        userId,
        roleId,
      })
      return { userId, roleId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["users", data.userId, "roles"],
      })
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })
}

/**
 * Fetch user permissions
 */
export function useUserPermissions(userId: string) {
  return useQuery({
    queryKey: ["users", userId, "permissions"],
    queryFn: async (): Promise<PermissionResponse[]> => {
      const response =
        await api.admin.getUserPermissionsApiV1AdminUsersUserIdPermissionsGet({
          userId,
        })
      return (unwrapStandardResponse<PermissionResponse[]>(response) ??
        []) as PermissionResponse[]
    },
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}
